import { useCallback, useEffect, useRef, useState } from 'react'
import { relayRequest } from '../transport/relay'
import type { PairedServer } from './serverList'

/**
 * tmux, on the machine you are about to create an agent on — owner, 2026-09-10: a
 * colleague paired a Mac with no tmux and Create Agent failed with no way forward.
 * Every agent is a tmux session, so a machine without tmux can pair and then do
 * nothing.
 *
 * This asks the machine `GET /system/tmux`; when tmux is present it renders NOTHING
 * (the form is fine). When it is missing it shows one banner with the way forward
 * for THAT OS:
 *   · the machine can install without a password (Homebrew, root, passwordless sudo)
 *     → an Install button that runs it and re-checks until tmux appears;
 *   · it needs a password → an "Open a terminal" button that drops the exact command
 *     into a shell on that machine, where sudo can ask for the password properly;
 *   · no known package manager → the command to run by hand.
 *
 * `onOpenShell(command)` opens the Cmd+J drawer's shell on this machine with the
 * command pre-typed (App wires it to shellTabs.openShellTab).
 */
type Report = {
  installed: boolean
  version: string | null
  os: string
  manager: string | null
  command: string | null
  canAuto: boolean
  reason: string
}
type InstallResult = {
  ok: boolean
  installed: boolean
  version: string | null
  reason: string
  command: string | null
  log?: string
}

async function ask(server: PairedServer): Promise<Report | 'unreachable'> {
  try {
    const a = await relayRequest('GET', '/system/tmux', null, undefined, server.server_id)
    return a.status === 200 ? (a.body as Report) : 'unreachable'
  } catch {
    return 'unreachable'
  }
}

export function TmuxGate({
  server,
  onOpenShell,
  onReady,
}: {
  server: PairedServer
  onOpenShell: (command: string) => void
  /** Called with true once tmux is confirmed present (so a parent may re-enable Create). */
  onReady?: (installed: boolean) => void
}) {
  const [report, setReport] = useState<Report | 'unreachable' | null>(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const refresh = useCallback(async () => {
    const r = await ask(server)
    if (!alive.current) return r
    setReport(r)
    if (r !== 'unreachable') onReady?.(r.installed)
    return r
  }, [server, onReady])

  // Ask once per machine. A machine that is not chosen is not asked.
  useEffect(() => {
    setReport(null)
    setNote(null)
    void refresh()
  }, [server.server_id, refresh])

  const install = useCallback(async () => {
    setBusy(true)
    setNote(null)
    try {
      let res: InstallResult | null = null
      try {
        const a = await relayRequest('POST', '/system/tmux/install', {}, undefined, server.server_id)
        res = a.status === 200 ? (a.body as InstallResult) : null
      } catch {
        // The install can outlast the relay's patience; the poll below is the truth.
      }
      if (res?.reason === 'needs_sudo' && res.command) {
        onOpenShell(res.command)
        setNote('This machine needs your password. A terminal opened with the command — press Enter and type your password.')
        return
      }
      // Poll detection until tmux appears or we give up — the install may still be
      // running on the box after the POST returned (or after it timed out).
      const deadline = Date.now() + 150_000
      for (;;) {
        const r = await refresh()
        if (!alive.current) return
        if (r !== 'unreachable' && r.installed) {
          setNote(null)
          return
        }
        if (Date.now() > deadline) {
          const cmd = (r !== 'unreachable' && r.command) || res?.command || 'your package manager'
          setNote(`Still not installed. Open a terminal and run: ${cmd}`)
          return
        }
        await new Promise((r) => setTimeout(r, 3_000))
      }
    } finally {
      if (alive.current) setBusy(false)
    }
  }, [server.server_id, onOpenShell, refresh])

  // Nothing to say: still asking, tmux present, or the machine is away (the create
  // flow already reports an offline machine in its own words).
  if (report === null || report === 'unreachable' || report.installed) return null

  const osName =
    report.os === 'darwin' ? 'macOS' : report.os === 'linux' ? 'Linux' : report.os
  return (
    <div className="tmux-gate">
      <div className="tmux-gate-hd">tmux is not installed on {server.name}</div>
      <p className="tmux-gate-note">
        An agent runs inside tmux, so {server.name} needs it before it can start one
        {report.manager ? ` (${osName}, via ${report.manager})` : ''}.
      </p>
      {report.reason === 'no_manager' ? (
        <p className="tmux-gate-note">
          No package manager was found on this machine. Install tmux by hand, then try again.
        </p>
      ) : report.canAuto ? (
        <button type="button" className="tmux-gate-btn" onClick={() => void install()} disabled={busy}>
          {busy ? 'Installing tmux…' : 'Install tmux'}
        </button>
      ) : (
        <button
          type="button"
          className="tmux-gate-btn"
          onClick={() => report.command && onOpenShell(report.command)}
          disabled={busy}
        >
          Open a terminal to install
        </button>
      )}
      {report.command && !report.canAuto && report.reason !== 'no_manager' && (
        <p className="tmux-gate-cmd">
          <code>{report.command}</code>
        </p>
      )}
      {note && <p className="tmux-gate-note">{note}</p>}
    </div>
  )
}
